import { useCallback, useRef } from "react";
import { useAppDispatch } from "hooks/store";
import { ManagementApiResponseAdapter } from "util/api";
import useGeneralActions from "features/general/useActions";
import type { RequestSettings, UseRequestArgs } from "./types";
import { getNotificationSettings, toResourceResponse, withDefaults } from "./helpers";

export const useRequest = <TResponse, TRequestParams>({
    thunk,
    ...settings
}:
    & UseRequestArgs<TResponse, TRequestParams>
    & RequestSettings
) => {
    const dispatch = useAppDispatch();
    const { showNotication } = useGeneralActions();

    const helpers = {
        thunk,
        settings,
        showNotication,
        dispatch
    };

    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;

    return useCallback((
        params: TRequestParams,
        requestSettings: RequestSettings = {}
    ) => {
        const {
            thunk,
            settings,
            showNotication,
            dispatch
        } = helpersRef.current;

        const {
            shouldNotify,
            shouldNotifyOnReject,
        } = withDefaults(settings, requestSettings);

        const request = dispatch(thunk(params));

        return [
            request
                .then(result => {
                    if (thunk.fulfilled.match(result)) {
                        const payload = ManagementApiResponseAdapter.from<TResponse>(
                            result.payload,
                            { ok: true, status: 200, statusText: 'OK' }
                        );

                        if (shouldNotify(payload)) {
                            showNotication(
                                getNotificationSettings(payload)
                            );
                        }

                        return payload as TResponse;
                    }

                    if (!result.meta.aborted && !result.meta.condition) {
                        toResourceResponse(result.payload).then(rejectedPayload => {
                            if (shouldNotifyOnReject(rejectedPayload)) {
                                showNotication(
                                    getNotificationSettings(rejectedPayload)
                                );
                            }
                        });
                    }
                }),

            request
        ];
    }, []);
};

export default useRequest;
